<template>
    <div class="discover">
        <h1 class="heading">What's New?</h1>

        <div class="discover__utilityBar">
            <div class="search">
                <i class="fas fa-search"></i>
                <input
                    type="search"
                    placeholder="Search"
                    v-model="searchString"
                    @input="searchByString"
                />
            </div>
            <app-button
                v-if="userIsAdmin"
                classes="btn btn-orange"
                @click.native="openPostAdminModal(null)"
                text="Add Post"
            />
        </div>


        <div class="page-controls">
            <ul
                v-if="deviceWidth > 768"
                class="cardFilter --posts"
            >
                <li
                    :class="{ '--selected': sideFilterSelection == 'all' }"
                    @click="onPostFilterChange('all')"
                >
                    All
                </li>
                <li
                    :class="{
                                '--selected': sideFilterSelection == 'ideas',
                            }"
                    @click="onPostFilterChange('ideas')"
                >
                    Ideas
                </li>
                <li
                    :class="{
                                '--selected': sideFilterSelection == 'polls',
                            }"
                    @click="onPostFilterChange('polls')"
                >
                    Polls
                </li>
                <li
                    :class="{
                                '--selected':
                                    sideFilterSelection == 'announcements',
                            }"
                    @click="onPostFilterChange('announcements')"
                >
                    Announcements
                </li>
            </ul>

            <div
                v-if="deviceWidth < 769"
                class="multiselect-wrapper"
            >
                <multi-select
                    v-model="mobileFilter"
                    selectLabel=""
                    deselectLabel=""
                    @input="onMobileFilterChange()"
                    :preserve-search="true"
                    :options="['All', 'Products', 'Ideas', 'Polls', 'Announcements']"
                    :taggable="false"
                />
            </div>
        </div>

        <!-- Desktop Container -->
        <transition
            v-if="!storeIsLoading"
            name="fadeInOut"
            appear
        >
        <div
            v-if="deviceWidth > 768"
            class="content-container"
        >
            <div class="resources-products">
                <transition-group
                    tag="div"
                    name="list"
                    class="cardRow"
                >
                    <div
                        v-for="item in filteredCards"
                        :key="item.id"
                        v-if = "checkStatus(item) === true"
                        class="cardContainer"
                    >
                        <router-link
                            v-if="item.hasOwnProperty('productImages')"
                            :to="{ path: `/product/${item.id}` }"
                        >
                            <product-card :details="item">
                                <slot>
                                    <router-link
                                        :to="`/product/${item.id}`"
                                        class="btn btn-orange"
                                    >
                                        Order
                                    </router-link>
                                    <ul class="statRow__statList">
                                        <li>
                                            <star-rating
                                                :star-size="25"
                                                :show-rating="false"
                                                :increment="0.5"
                                                :read-only="true"
                                                :rating="item.rating"
                                            />
                                        </li>
                                    </ul>
                                </slot>
                            </product-card>
                        </router-link>

                        <resource-card
                            v-else
                            :resource="item"
                        />
                    </div>
                    <div
                        class="emptyRowFiller"
                        key="filler1"
                    ></div>
                    <div
                        class="emptyRowFiller"
                        key="filler2"
                    ></div>
                </transition-group>
            </div>

            <div class="posts">
                <discover-post
                    v-for="post in discoverPosts"
                    :key="post.id"
                    :details="post"
                    :active-user="users.activeUser"
                    @update-vote="onPollVoteUpdate($event, post)"
                    @edit-post="openPostAdminModal(post)"
                />
            </div>
        </div>

        <!-- Mobile Container -->
        <div
            v-else
            class="mobile-group"
        >
            <div
                v-if="mobileFilter === 'All' || mobileFilter === 'Products'"
                v-for="item in filteredCards"
                :key="`s-${item.id}`"
                class="cardContainer"
            >
                <router-link
                    v-if="item.hasOwnProperty('productImages')"
                    :to="{ path: `/product/${item.id}` }"
                >
                    <product-card :details="item">
                        <slot>
                            <router-link
                                :to="`/product/${item.id}`"
                                class="btn btn-orange"
                            >
                                Order
                            </router-link>
                            <ul class="statRow__statList">
                                <li>
                                    <star-rating
                                        :star-size="25"
                                        :show-rating="false"
                                        :increment="0.5"
                                        :read-only="true"
                                        :rating="item.rating"
                                    />
                                </li>
                            </ul>
                        </slot>
                    </product-card>
                </router-link>

                <resource-card
                    v-else
                    :resource="item"
                />
            </div>

            <discover-post
                v-if="mobileFilter === 'All' || mobileFilter === 'Ideas' || mobileFilter === 'Polls' || mobileFilter === 'Announcements'"
                v-for="post in discoverPosts"
                :key="post.id"
                :details="post"
                :active-user="users.activeUser"
                @edit-post="openPostAdminModal(post)"
            />
        </div>
        </transition>
        <loading-dots
            v-else
            loading-text="Loading content"
        />

        <transition name="addDiscoverPostModal">
            <add-edit-discover-post
                v-if="addEditDiscoverPostShowing"
                :post="selectedPost"
                :action="postModalAction"
                @close="closeAdminModal()"
            />
        </transition>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
// components
import StarRating from "vue-star-rating";
import DiscoverPost from "@/components/DiscoverPage/DiscoverPost";
import ProductCard from "@/components/Shared/ProductCard";
import ResourceCard from "@/components/Shared/ResourceCard";

export default {
    name: "discover-page",
    components: {
        "product-card": ProductCard,
        "resource-card": ResourceCard,
        "star-rating": StarRating,
        "discover-post": DiscoverPost,
        "app-button": () => import("@/components/Shared/AppButton"),
        "add-edit-discover-post": () =>
            import("@/components/DiscoverPage/AddEditDiscoverPost"),
        "multi-select": () => import("vue-multiselect"),
        "loading-dots": () => import("@/components/Shared/LoadingDots"),
    },
    data() {
        return {
            showcaseFilterSelection: "all",
            sideFilterSelection: "all",
            addEditDiscoverPostShowing: false,
            selectedPost: null,
            searchString: "",
            filteredCards: [],
            postModalAction: "add",
            deviceWidth: window.innerWidth,
            mobileFilter: "All",
            storeIsLoading: true,
        };
    },
    computed: {
        ...mapState(["users", "products", "discover", "auth"]),
        ...mapGetters([
            "allDiscoverProducts",
            "allIdeas",
            "activePolls",
            "allPolls",
            "allAnnouncements",
            "allPosts",
        ]),

        userIsAdmin() {
            try {
                return this.users.activeUser.userRoles.includes(
                    "ROLE_MARKETING_EMPLOYEE"
                );
            } catch (error) {
                /* shhhh */
            }
            return false;
        },

        polls() {
            if (this.userIsAdmin) {
                return this.allPolls;
            }
            return this.activePolls;
        },

        discoverPosts() {
            if (this.sideFilterSelection == "ideas") {
                return this.allIdeas;
            }
            if (this.sideFilterSelection == "polls") {
                return this.polls;
            }
            if (this.sideFilterSelection == "announcements") {
                return this.allAnnouncements;
            }
            return this.allPosts;
        },
    },
    methods: {
        ...mapActions(["addCommentToPost", "updateProducts", "fetchPosts"]),
        ...mapMutations([
            "ADD_COMMENT_TO_POST",
            "TOGGLE_EMPLOYEE_LIKE",
            "UPDATE_POLL_VOTES",
            "DELETE_POST",
            "MANAGE_POST",
        ]),
        filterCards() {
            const queryProducts = () => {
                const tempCards = JSON.parse(
                    JSON.stringify([...this.allDiscoverProducts])
                );

                if (this.searchString !== "") {
                    return tempCards.filter(
                        (card) =>
                            card.name
                                .toLowerCase()
                                .includes(this.searchString.toLowerCase()) ||
                            card.description
                                .toLowerCase()
                                .includes(this.searchString.toLowerCase())
                    );
                }
                return tempCards;
            };

            if (this.showcaseFilterSelection === "products") {
                this.filteredCards = queryProducts();
            } else {
                this.filteredCards = [...queryProducts()];
            }
        },

      checkStatus(product) {
          if(product.isActive == null || product.isActive === 1){
            return true;
          }
      },


      searchByString() {
            this.filterCards();
        },

        onPostFilterChange(type) {
            switch (type) {
                case "all": {
                    this.sideFilterSelection = "all";
                    break;
                }
                case "ideas": {
                    this.sideFilterSelection = "ideas";
                    break;
                }
                case "polls": {
                    this.sideFilterSelection = "polls";
                    break;
                }
                case "announcements": {
                    this.sideFilterSelection = "announcements";
                    break;
                }
            }
        },

        onMobileFilterChange() {
            switch (this.mobileFilter) {
                case "Products": {
                    this.showcaseFilterSelection = "products";
                    break;
                }
                case "Ideas": {
                    this.sideFilterSelection = "ideas";
                    break;
                }
                case "Polls": {
                    this.sideFilterSelection = "polls";
                    break;
                }
                case "Announcements": {
                    this.sideFilterSelection = "announcements";
                    break;
                }
                case "All": {
                    this.showcaseFilterSelection = "resources";
                    this.sideFilterSelection = "all";
                    break;
                }
            }
            this.filterCards();
        },

        openPostAdminModal(post) {
            this.addEditDiscoverPostShowing = false;
            this.selectedPost = post;
            if (!this.selectedPost) {
                this.postModalAction = "add";
            } else {
                this.postModalAction = "edit";
            }
            this.addEditDiscoverPostShowing = true;
        },

        closeAdminModal() {
            this.selectedPost = null;
            this.addEditDiscoverPostShowing = false;
        },

        onResize() {
            this.deviceWidth = window.innerWidth;
        },
    },
    sockets: {
        manageDiscoverPost(post) {
            this.MANAGE_POST(post);
        },

        discoverPostComment(data) {
            const { postId, comment } = data;
            this.ADD_COMMENT_TO_POST({ postId, comment });
        },

        likeDiscoverPost(data) {
            const { postId, employeeNumber } = data;
            this.TOGGLE_EMPLOYEE_LIKE({ postId, employeeNumber });
        },

        voteDiscoverPoll(data) {
            const { postId, pollOptions } = data;
            this.UPDATE_POLL_VOTES({ postId, pollOptions });
        },

        deleteDiscoverPost(postId) {
            this.DELETE_POST(postId);
        },
    },
    async mounted() {
        await this.updateProducts();
        await this.fetchPosts();
        this.filterCards();
        this.storeIsLoading = false;
        // set our resize listner for reactive responsive styles
        this.$nextTick(() => {
            window.addEventListener("resize", this.onResize);
        });
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.onResize);
    },
};
</script>

<style lang="scss" scoped>
.discover {
    padding: $standard-view;
    @include breakpoint(ipadPro) {
        padding: 4rem 4rem 10rem;
    }
    @include breakpoint(mobile) {
        padding: 4rem 2rem 10rem;
    }
    &__utilityBar {
        display: flex;
        justify-content: space-between;
        margin: 2rem 0 3rem;
        .search {
            position: relative;
            input {
                width: 24.5rem;
                height: 2.6rem;
                padding: 0.5rem 0.5rem 0.25rem 2.8rem;
                border-radius: 5rem;
                border: 1px solid $gray;
                @include breakpoint(desktop) {
                    width: 22rem;
                }
                @include breakpoint(tablet-port) {
                    width: 17rem;
                }
                &:focus,
                &:active {
                    outline: none;
                }
            }
            .fa-search {
                position: absolute;
                top: 0.5rem;
                left: 0.5rem;
                color: $gray;
            }
        }
        button {
            margin-left: 1.5rem;
            @include breakpoint(ipadPro) {
                display: none;
            }
            &:hover {
                background-color: $orange;
                color: #fff;
            }
        }
    }

    .page-controls {
        display: flex;
        justify-content: end;

      @include breakpoint(tablet-land) {
        justify-content: start;
      }

        .multiselect-wrapper {
            width: 17rem;
            margin-bottom: 3rem;
        }
    }
    .cardFilter {
        display: flex;
        margin: 0 0 0 0.5rem;
        .--selected {
            color: #000;
        }
        li {
            margin-right: 4rem;
            font-weight: 600;
            color: $gray;
            cursor: pointer;
        }
        &.--posts {
            justify-content: flex-end;
            margin: 0 0.5rem 3rem 4rem;
            li {
                margin-left: 4rem;
                margin-right: 0;
            }
        }
    }
}

// content layout
.content-container {
    display: flex;
}
.mobile-group {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}
.resources-products {
    width: 75%;
}
.posts {
    width: 25%;
    min-width: 400px;
    @include breakpoint(laptop) {
        min-width: 350px;
    }
}

// products/resources
.cardContainer {
    width: 21%;
    min-width: 30rem;
    margin-right: 8rem;
    @include breakpoint(laptop) {
        margin-right: 2rem;
        min-width: unset;
        width: auto;
    }
}
.cardRow {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    padding: 0 4px;
}
.emptyRowFiller {
    width: 21%;
    min-width: 30rem;
    height: 0;
}

::v-deep p.loading {
    margin-top: 4rem;
    text-align: left;
}


@include slideDownEnter("addDiscoverPostModal", 35%, 50%);
</style>
